<script setup lang="ts">
import { PaginationData } from '@/@types/order'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Title } from '@/components/ui/title'
import LightLayout from '@/layouts/LightLayout.vue'
import { Head, Link, useForm } from '@inertiajs/vue3'
import { Search } from 'lucide-vue-next'
import { computed, ref } from 'vue'

interface YazoRequestLog {
    id: number
    external_id: string | null
    mailing_id: number | null
    mailing_email: string | null
    http_status: number
    accepted: boolean
    error_message: string | null
    request_payload: Record<string, unknown>
    response_payload: Record<string, unknown>
    created_at: string
}

const props = defineProps<{
    logs: YazoRequestLog[]
    pagination: PaginationData
    filters: {
        email?: string | null
    }
}>()

const expandedId = ref<number | null>(null)

const form = useForm({
    email: props.filters.email || '',
})

const acceptedCount = computed(() => props.logs.filter((log) => log.accepted).length)
const rejectedCount = computed(() => props.logs.filter((log) => !log.accepted).length)

function applyFilters() {
    form.get('/admin/yazo_request_logs', { preserveScroll: true })
}

function clearFilters() {
    form.email = ''
    form.get('/admin/yazo_request_logs', { preserveScroll: true })
}

function pageHref(page: number) {
    const params = new URLSearchParams()
    if (form.email) params.set('email', form.email)
    if (page > 1) params.set('page', String(page))
    const qs = params.toString()
    return qs ? `/admin/yazo_request_logs?${qs}` : '/admin/yazo_request_logs'
}

function toggleExpanded(id: number) {
    expandedId.value = expandedId.value === id ? null : id
}

function formatJson(value: Record<string, unknown>) {
    return JSON.stringify(value, null, 2)
}

function formatDate(value: string) {
    return new Date(value).toLocaleString()
}
</script>

<template>
    <Head :title="$t('Yazo request logs')" />

    <LightLayout contentClasses="bg-slate-50">
        <main class="container flex flex-col gap-8">
            <Title class="flex items-center gap-2 font-sans font-semibold">
                {{ $t('Yazo request logs') }}
            </Title>

            <Card class="border-0 shadow-sm">
                <CardContent class="pt-6">
                    <form class="flex flex-col gap-4 md:flex-row md:items-end" @submit.prevent="applyFilters">
                        <div class="min-w-[220px] flex-1">
                            <label class="mb-1 block text-sm font-medium text-slate-600" for="yazo-email">
                                {{ $t('Email') }}
                            </label>
                            <div class="relative">
                                <Search
                                    class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400"
                                />
                                <Input
                                    id="yazo-email"
                                    v-model="form.email"
                                    type="text"
                                    :placeholder="$t('Filter by mailing email')"
                                    class="pl-10"
                                />
                            </div>
                        </div>
                        <div class="flex gap-2">
                            <Button type="submit">{{ $t('Filter') }}</Button>
                            <Button type="button" variant="outline" @click="clearFilters">{{ $t('Clear') }}</Button>
                        </div>
                    </form>
                </CardContent>
            </Card>

            <p class="text-sm text-slate-600">
                {{ $t('Showing the latest Yazo mailing update requests') }}
                ({{ pagination.total }}) —
                <span class="text-green-700">{{ acceptedCount }} {{ $t('accepted') }}</span>,
                <span class="text-red-700">{{ rejectedCount }} {{ $t('rejected') }}</span>
                <span class="text-slate-500">
                    · {{ $t('Page') }} {{ pagination.current_page }} / {{ pagination.last_page }}
                </span>
            </p>

            <section class="flex flex-col gap-4">
                <Card v-for="log in logs" :key="log.id" class="bg-white shadow-sm">
                    <CardHeader>
                        <CardTitle class="flex flex-wrap items-center justify-between gap-2 text-base">
                            <div class="flex flex-col gap-1">
                                <span>
                                    <strong>{{ $t('External ID') }}:</strong>
                                    {{ log.external_id || '—' }}
                                </span>
                                <span class="text-sm font-normal text-slate-500">
                                    {{ formatDate(log.created_at) }}
                                </span>
                            </div>
                            <span
                                class="rounded px-2 py-1 text-sm font-medium"
                                :class="log.accepted ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                            >
                                {{ log.accepted ? $t('Accepted') : $t('Rejected') }}
                                · HTTP {{ log.http_status }}
                            </span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent class="flex flex-col gap-3 text-sm">
                        <div v-if="log.mailing_email">
                            <strong>{{ $t('Mailing') }}:</strong>
                            #{{ log.mailing_id }} — {{ log.mailing_email }}
                        </div>

                        <div v-if="log.error_message" class="text-red-700">
                            <strong>{{ $t('Reason') }}:</strong>
                            {{ log.error_message }}
                        </div>

                        <button
                            type="button"
                            class="w-fit text-left text-sm font-medium text-slate-700 underline"
                            @click="toggleExpanded(log.id)"
                        >
                            {{ expandedId === log.id ? $t('Hide payload') : $t('Show payload') }}
                        </button>

                        <div v-if="expandedId === log.id" class="grid gap-3 md:grid-cols-2">
                            <div>
                                <strong>{{ $t('Request') }}</strong>
                                <pre class="mt-1 overflow-auto rounded bg-slate-100 p-3 text-xs">{{ formatJson(log.request_payload) }}</pre>
                            </div>
                            <div>
                                <strong>{{ $t('Response') }}</strong>
                                <pre class="mt-1 overflow-auto rounded bg-slate-100 p-3 text-xs">{{ formatJson(log.response_payload) }}</pre>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <p v-if="!logs.length" class="text-sm text-slate-500">
                    {{ $t('No Yazo requests logged yet') }}
                </p>

                <div v-if="pagination.last_page > 1" class="flex flex-wrap items-center justify-center gap-2">
                    <Button
                        v-if="pagination.current_page > 1"
                        variant="outline"
                        size="sm"
                        :as="Link"
                        :href="pageHref(1)"
                    >
                        «
                    </Button>
                    <Button
                        v-if="pagination.current_page > 1"
                        variant="outline"
                        size="sm"
                        :as="Link"
                        :href="pageHref(pagination.current_page - 1)"
                    >
                        ‹
                    </Button>
                    <span class="px-2 text-sm text-slate-600">
                        {{ pagination.current_page }} / {{ pagination.last_page }}
                    </span>
                    <Button
                        v-if="pagination.current_page < pagination.last_page"
                        variant="outline"
                        size="sm"
                        :as="Link"
                        :href="pageHref(pagination.current_page + 1)"
                    >
                        ›
                    </Button>
                    <Button
                        v-if="pagination.current_page < pagination.last_page"
                        variant="outline"
                        size="sm"
                        :as="Link"
                        :href="pageHref(pagination.last_page)"
                    >
                        »
                    </Button>
                </div>
            </section>
        </main>
    </LightLayout>
</template>
